import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { InlineCTA } from "@/components/InlineCTA";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/masonry-walkway-bloomfield-essex-county-nj.webp";
import signsImage from "@/assets/retaining-wall-bloomfield-essex-county-nj.webp";
import materialsImage from "@/assets/patio-paver-livingston-essex-county-nj.webp";
import permitsImage from "@/assets/retaining-wall-west-orange-essex-county-nj.webp";
import installImage from "@/assets/outdoor-kitchen-montclair-nj.webp";

const faqs = [
  {
    question: "Do I need a permit for a paver patio or retaining wall in Essex County?",
    answer:
      "It depends on the town and the scope of the project. Most Essex County municipalities require permits for structural retaining walls or any project that changes the grading of your yard. Since all 22 towns operate their own building departments, requirements and timelines vary. A paver patio on a flat lot may not need one in some towns, while others require it regardless. We check with the local building department before every project and handle the application for you.",
  },
  {
    question: "What time of year is best for outdoor masonry work in Essex County?",
    answer:
      "Spring through late fall is the best window for most outdoor masonry projects. Concrete and mortar need consistent temperatures above 40°F to cure properly, so winter installations are limited. Most homeowners who contact us during the colder months get scheduled for an early spring start. Booking ahead of the season is a good idea since our schedule fills up quickly once the weather turns.",
  },
  {
    question: "How does Essex County's clay soil affect patio and wall installations?",
    answer:
      "Clay soil holds water and expands during freeze-thaw cycles, which can cause shifting and settling. To account for that, we excavate deeper than we would on sandier ground and build a thicker compacted stone base. Proper drainage behind retaining walls is also more involved on clay. Skipping those steps is why a lot of patios and walls in this area fail within a few years of installation.",
  },
  {
    question: "Can an outdoor kitchen or fireplace be built on a sloped Essex County lot?",
    answer:
      "Yes, with proper site preparation. Hillside properties near South Mountain Reservation or along the Watchung ridgeline often need terraced retaining walls and a graded, level pad before any structure goes up. The design and engineering depend on the degree of slope and the soil conditions. We evaluate all of that during the on-site walkthrough.",
  },
  {
    question: "How long does a typical paver patio or retaining wall project take in this area?",
    answer:
      "A small to mid-sized paver patio usually takes about 3 to 5 days once work begins. Larger retaining walls or outdoor kitchen builds can run 2 to 4 weeks depending on scope and site conditions. Keep in mind that permitting timelines are separate. Some Essex County towns turn permits around in a few days, while others may take up to 20 business days.",
  },
  {
    question: "Are there HOA or historic district rules that affect masonry material choices?",
    answer:
      "Some Essex County neighborhoods do have material or color restrictions. Llewellyn Park in West Orange is a National Register Historic District, and Montclair's historic preservation ordinance covers several designated districts. Glen Ridge has similar considerations. If your property falls within one of these areas, it is a good idea to check with the local zoning office before selecting materials. We can help guide you through that process.",
  },
];

const municipalities = [
  "Belleville", "Bloomfield", "Caldwell", "Cedar Grove", "East Orange",
  "Essex Fells", "Fairfield", "Glen Ridge", "Irvington", "Livingston",
  "Maplewood", "Millburn", "Montclair", "Newark", "North Caldwell",
  "Nutley", "Orange", "Roseland", "South Orange", "Verona",
  "West Caldwell", "West Orange",
];

export default function EssexCounty() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Masonry Contractor Essex County NJ | Masonry Pro</title>
        <meta name="description" content="Planning a patio, retaining wall, or outdoor kitchen in Essex County, NJ? See how we handle the clay soil, permits, and slopes across all 22 towns here." />
        <meta property="og:description" content="Planning a patio, retaining wall, or outdoor kitchen in Essex County, NJ? See how we handle the clay soil, permits, and slopes across all 22 towns here." />
        <meta property="og:title" content="Masonry Contractor Essex County NJ | Masonry Pro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
            }))
          })}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/essex-county.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/essex-county.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Masonry contractor serving Essex County, NJ" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <MapPin className="h-6 w-6" style={{ color: '#d4a853' }} />
                  <span style={{ color: '#d4a853', fontWeight: 500 }}>Essex County, NJ</span>
                </div>
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  <Link to="/paving-services/paver-patios" className="hover:text-secondary transition-colors">Paver Patios</Link>,{" "}
                  <Link to="/masonry-services/retaining-walls" className="hover:text-secondary transition-colors">Retaining Walls</Link>, and{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Masonry Installations</span> Across Essex County, NJ
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', maxWidth: '42rem', margin: '0 auto', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Serving all 22 municipalities with expert hardscape and masonry work built for Essex County's clay soil and terrain.
                </p>
              </div>
            </div>
          </section>

          {/* Intro Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  <a href="https://en.wikipedia.org/wiki/Essex_County,_New_Jersey" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Essex County</a><sup className="text-secondary">1</sup>{" "}
                  covers 22 municipalities across some of the most varied terrain in North Jersey. From the flat blocks of Newark to the wooded hillsides of Cedar Grove and Verona, lot sizes and ground conditions change from one town to the next. That range matters when it comes to masonry installations in Essex County.
                </p>
                <p>
                  This page covers{" "}
                  <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">paver patios</Link>,{" "}
                  <Link to="/paving-services/paver-walkways" className="text-secondary hover:underline">walkways</Link>,{" "}
                  <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining walls</Link>,{" "}
                  <Link to="/concrete-services" className="text-secondary hover:underline">concrete</Link> and{" "}
                  <Link to="/paving-services/paver-driveways" className="text-secondary hover:underline">driveway work</Link>,{" "}
                  <Link to="/masonry-services/fire-pits" className="text-secondary hover:underline">fireplaces</Link>,{" "}
                  <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">outdoor kitchens</Link>,{" "}
                  <Link to="/waterproofing-services/basement-waterproofing" className="text-secondary hover:underline">basement waterproofing</Link>, and{" "}
                  <Link to="/waterproofing-services/patio-waterproofing" className="text-secondary hover:underline">patio waterproofing</Link> installations. Each one is shaped by local soil, slopes, and weather. And in this part of the state, all three can work against you if they're not accounted for.
                </p>
                <p>
                  For homeowners pricing out a backyard upgrade, here's a closer look at <Link to="/pro-tips/paver-patio-cost-north-jersey" className="text-secondary hover:underline">how much a paver patio runs here</Link> and what tends to push the number up or down.
                </p>
                <p>
                  Essex County sits on glacial clay soils that shift with moisture and freeze-thaw cycles. That directly affects how we prep a base for patios, walls, and driveways. Homes in towns like Montclair, West Orange, and Maplewood often sit on uneven lots with mature tree roots and aging drainage that has to be addressed before any work begins.
                </p>
                <p>
                  Every municipality in Essex County runs its own building department. Livingston has a different permit process from Bloomfield. South Orange handles things differently than Verona. We deal with that on every project, so you don't have to sort it out yourself.
                </p>
                <p>
                  Tight lot lines in neighborhoods such as Glen Ridge or Verona can also affect how equipment is staged. Side yard access, shared driveways, and setback requirements all factor into planning before any digging starts.
                </p>
                <p>
                  If you're considering a project on your Essex County property, we can schedule an on-site walkthrough to go over your scope and timeline.
                </p>
              </div>
            </div>
          </section>

          {/* CTA 1 */}
          <InlineCTA heading="Thinking About a Project?" highlightedHeading="Check Availability" description="Call or text us directly or scroll down to request your free estimate." />

          {/* Property Evaluation Section - image left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={signsImage} alt="Retaining wall installation on a residential property in Bloomfield, Essex County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Evaluating Your Property for{" "}
                    <span className="text-gradient-gold">Hardscape and Masonry Work</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      If you're in Livingston, Cedar Grove, or Caldwell and your yard slopes toward the house instead of away from it, that's worth looking into before it gets worse. The same goes for walkways that have settled, retaining walls that are starting to lean, or water that pools near your foundation after a heavy rain.
                    </p>
                    <p>
                      A lot of Essex County homes were built in the early-to-mid 1900s. The original grading on many of these properties was designed for a different time. Over the decades, soil settles, roots grow, and drainage patterns shift. What worked 60 or 70 years ago may now be sending water right toward your foundation.
                    </p>
                    <p>Here's what to watch for on your own property:</p>
                    <ul className="list-disc list-inside space-y-2">
                      <li>Walkway sections that rock or sit unevenly when you step on them</li>
                      <li>Retaining walls with visible tilting or gaps forming between blocks</li>
                      <li>Water sitting against the foundation or pooling in low spots after rain</li>
                      <li>Cracked concrete along driveways or sidewalks that gets wider each year</li>
                    </ul>
                    <p>
                      If water is reaching your basement walls or floor, that may point to a <Link to="/waterproofing-services/basement-waterproofing" className="text-secondary hover:underline">waterproofing</Link> issue worth looking into.
                    </p>
                    <p>
                      These are signs the ground underneath is moving. And in most cases, the fix involves addressing what's below the surface, not just patching what you can see.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Materials Section - text left, image right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Choosing the Right Materials for{" "}
                    <span className="text-gradient-gold">Clay Soil and Freeze-Thaw Cycles</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      If you're upgrading a <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">patio</Link>, <Link to="/paving-services/paver-driveways" className="text-secondary hover:underline">paver driveway</Link>, <Link to="/concrete-services/driveways" className="text-secondary hover:underline">concrete driveway</Link>, or <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining wall</Link> in Maplewood, South Orange, or Nutley, the material you pick matters more than most people expect. Not every product holds up the same way on clay subgrade, and Essex County's winters don't leave much room for shortcuts.
                    </p>
                    <p>
                      Concrete pavers, natural bluestone, and segmental retaining wall block are the most common options we install. Each one handles moisture, weight, and ground movement differently. Concrete pavers offer flexibility because individual units can shift slightly without cracking. Bluestone looks great but needs a stable, well-drained base to avoid splitting during freeze cycles. Segmental wall block is engineered to interlock under pressure, which is critical on slopes where soil exerts significant pressure against the structure.
                    </p>
                    <p>
                      Essex County can see 40 or more freeze-thaw cycles in a single winter<sup className="text-secondary">3</sup>. That means the ground beneath your patio or wall is expanding and contracting repeatedly from late fall through early spring. The joint compound between pavers and the drainage behind walls must be selected to accommodate that kind of movement.
                    </p>
                    <p>
                      One issue that often arises in Montclair and Glen Ridge is material matching. Homes with original Tudor and Colonial facades may need stone or brick that complements the existing look. Color, texture, and cut style all factor in. We can walk you through samples on-site so you see how each option looks against your home's exterior before anything is ordered.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={materialsImage} alt="Paver patio installation in Livingston, Essex County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </section>

          {/* Pre-Project Preparation - image left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={permitsImage} alt="Retaining wall construction in West Orange, Essex County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    What to Expect Before Your{" "}
                    <span className="text-gradient-gold">Patio, Wall, or Outdoor Kitchen</span> Project Begins
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      If your family in Bloomfield, West Caldwell, or Fairfield is planning a backyard project for spring or summer, there are a few things that need to happen before any equipment shows up.
                    </p>
                    <p>
                      First, New Jersey law requires calling NJ One Call at 811 at least three business days before any outdoor digging<sup className="text-secondary">4</sup>. That applies to every residential property, regardless of the project size. Underground utilities such as gas, electric, and water lines are marked so the crew knows exactly where they are before excavation begins.
                    </p>
                    <p>
                      Permits are the other piece. Each Essex County town handles this through its own building department<sup className="text-secondary">2</sup>. Verona has a different review process than Roseland. Essex Fells may move faster than Livingston. Structural retaining walls and projects that change the grading of your yard almost always require a permit. We handle the applications, but approval timelines vary. Some towns process permits in a few days. Others can take up to 20 business days.
                    </p>
                    <p>
                      If your property has narrow side yards or a shared driveway, equipment staging needs extra planning. A skid steer or mini excavator occupies space, and materials such as pallets of pavers or crushed stone need a place to sit during the project. On tighter lots, we may need to adjust delivery schedules or stage materials in phases.
                    </p>
                    <p>
                      It also helps to give your neighbors a heads-up before work begins. Especially on properties where access runs close to the lot line. A quick conversation goes a long way and avoids any surprises once the crew is on-site.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA 2 */}
          <InlineCTA heading="Questions About Permits?" highlightedHeading="We Can Help" description="Give us a call or text, or fill out the form below, and we handle permits across all 22 Essex County towns." />

          {/* Installation Section - text left, image right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  How Paver Patios, Retaining Walls, and Fireplaces Are{" "}
                  <span className="text-gradient-gold">Installed on Local Properties</span>
                </h2>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-6 text-lg text-muted-foreground">
                    <p>
                      Whether you're adding a <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">paver patio</Link> on a flat lot in Belleville or building a <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining wall</Link> on a slope near South Mountain Reservation, the installation process follows the same basic principle: what goes underneath determines how long everything on top will last.
                    </p>
                    <p>
                      Every project starts with excavation down to a stable subgrade. That means removing topsoil, roots, and any loose fill until we reach solid ground. From there, we build up with layers of compacted crushed stone, graded to pitch water away from the home. On clay soils like what we see across most of Essex County, that base layer often needs to be thicker than what you'd use on sandy or well-drained ground.
                    </p>
                    <p>
                      Retaining walls on hillside lots need more than just block and base. Properties along the Watchung ridgeline are common candidates for geogrid reinforcement, which is a mesh layer buried in the soil behind the wall to hold it in place. Perforated drain tile and washed stone backfill go behind the wall to keep water from building up pressure against the structure.
                    </p>
                    <p>
                      <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">Outdoor kitchens</Link> and <Link to="/masonry-services/fire-pits" className="text-secondary hover:underline">fireplaces</Link> require a stable pad before any stone or block is installed. On sloped properties, that sometimes means building a terraced retaining wall first, then leveling a section for the structure.
                    </p>
                    <p>
                      Paver patios on flatter lots in towns like Nutley or Bloomfield follow the same base prep but with less grading work. Once the pavers are laid, we lock them in with edge restraint and polymeric sand, which is a jointing material that hardens between the paver joints and helps prevent weed growth and insect activity.
                    </p>
                  </div>
                  <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                    <img
                      src={installImage}
                      alt="Outdoor kitchen installation in Montclair, Essex County, NJ by Masonry Pro Construction"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Winter Maintenance Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  Protecting Your Investment Through{" "}
                  <span className="text-gradient-gold">Essex County's Harsh Winters</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Once your patio, wall, or outdoor kitchen is in place, winter is the season that tests it the hardest. Salt, snow load, and ice all take a toll on masonry surfaces, and how you maintain things during the cold months can affect how they look and perform for years.
                  </p>
                  <p>
                    Polymeric sand between paver joints can wear down over time, especially with heavy foot traffic or pressure washing. It's a good idea to check the joints each spring and reapply as needed. When clearing snow off a paver surface, use a plastic-edge shovel instead of metal. Metal blades can chip the paver faces and damage the joint sand.
                  </p>
                  <p>
                    <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">Retaining walls</Link> need attention too. Weep holes and drain outlets along the base of the wall must remain clear through fall and winter. If leaves, debris, or ice block those openings, water backs up behind the wall. That creates hydrostatic pressure, and during spring thaw it can push the wall forward. A quick check before the ground freezes and again after snowmelt can prevent that.
                  </p>
                  <p>
                    <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">Outdoor kitchens</Link> and <Link to="/masonry-services/fire-pits" className="text-secondary hover:underline">fireplaces</Link> with natural stone veneer can benefit from a breathable sealant applied before the first hard freeze. The sealant helps prevent moisture from getting into the stone and expanding as temperatures drop. It does not change the stone's appearance and typically lasts a few seasons before needing reapplication.
                  </p>
                  <p>
                    Most of this is basic upkeep, not major work. A little attention in the fall and spring goes a long way toward getting the full lifespan out of your project.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Service Area / Getting Here */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Getting to Our Team from Anywhere in{" "}
                  <span className="text-gradient-gold">Essex County</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground text-left">
                  <p>
                    We serve all 22 municipalities across Essex County, from Newark in the east to Fairfield in the northwest. Our shop is located at 36 Highview Ave in Totowa, just over the Passaic County line, which puts us within a short drive of most Essex County towns.
                  </p>
                  <p>
                    The Garden State Parkway runs north-south through the county and connects Irvington, Newark, East Orange, and Bloomfield. If you're coming from any of those areas, the Parkway to Route 46 West gets you to Totowa in about 20 minutes, depending on traffic.
                  </p>
                  <p>
                    Route 280 provides east-west access through the middle of the county, linking The Oranges, Livingston, and Roseland. From Livingston or Roseland, take 280 East to the Garden State Parkway North, then Route 46 West toward Totowa.
                  </p>
                  <p>
                    From the western side of the county, towns like Cedar Grove, Verona, and Caldwell are typically 10 to 15 minutes from our location. Fairfield residents can reach us in under 10 minutes via Route 46. Travel times within Essex County to our shop generally range from 10 to 30 minutes. For most projects though, we come to you.
                  </p>
                </div>
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-4">All 22 Municipalities We Serve</h3>
                  <div className="flex flex-wrap justify-center gap-3">
                    {municipalities.map((town) => (
                      <span
                        key={town}
                        className="bg-card rounded-full px-4 py-2 border border-border text-sm text-muted-foreground"
                      >
                        {town}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA 3 */}
          <CTASection serviceName="Essex County masonry" heading="Ready to Start?" highlightedHeading="Schedule Your Estimate" />

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection
            videoIds={["IaLfa_u0pFQ", "Fogsfyz7P18", "_8_1uw8ZHAk"]}
            title="See Our Essex County"
            highlightedTitle="Work in Action"
          />

          {/* FAQ Section - Card Grid */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Citations Accordion */}
          <section className="py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <Accordion type="single" collapsible>
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                    Source Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                      <li>
                        Essex County, New Jersey – Wikipedia. County geography, 22 municipalities, Watchung Mountains terrain, climate data.
                      </li>
                      <li>
                        Essex County Featured Post – Rutgers NJ Data Book. Confirmation of 22 municipalities and county formation history.
                      </li>
                      <li>
                        Freeze-Thaw Days in the Northeastern United States – Journal of Applied Meteorology and Climatology (1987). Annual freeze-thaw cycles ranging from 50+ inland NJ to 90+ at higher elevations.
                      </li>
                      <li>
                        New Jersey One Call (811) – NJ law requires three full business days' notice before any digging. Underground Facility Protection Act, N.J.S.A. 48:2-73 et seq.
                      </li>
                      <li>
                        Llewellyn Park Historic District – Wikipedia. National Register of Historic Places listing (1985), West Orange, Essex County.
                      </li>
                      <li>
                        Montclair Historic Preservation Ordinance – Township of Montclair Code, Chapter 347, Article XXIII. Historic Preservation Commission and designated districts.
                      </li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
